import Pagination from '@/shared/Table/Pagination'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import { DATE_FORMAT, DATE_MONTH_FORMAT, GANTT_CHART_COLOR as COLOR } from '@/libs/core/constants'
import inRange from 'lodash/inRange'
import isEmpty from 'lodash/isEmpty'
import throttle from 'lodash/throttle'
import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BorderedSection from './BorderedSection'
import DateBar from './DateBar'
import DateHeader from './DateHeader'
import EmptyColumn from './EmptyColumn'
import LegendItem from './LegendItem'
import PaddedText from './PaddedText'
import RowHeader from './RowHeader'

import globalLess from '@/global.less'

const ROW_HEIGHT = 60
const NAME_COL_WIDTH = 177

const generateMonths = (startDate, endDate) => {
  const startOfEarliest = moment(startDate).startOf('month')
  const endOfLatest = moment(endDate).endOf('month')

  const months = []
  const cursorDate = moment(startOfEarliest)

  while (cursorDate.isBefore(endOfLatest)) {
    months.push(moment(cursorDate).format(DATE_FORMAT))
    cursorDate.add(1, 'month')
  }

  return months
}

const getCursorFraction = (start, end) => {
  const startDate = moment(start)
  const endDate = moment(end).endOf('month')
  const monthDiff = endDate.diff(startDate, 'months') + 1
  const curDate = moment()

  if (endDate.diff(curDate, 'days') < 0 || curDate.diff(startDate, 'days') < 0) {
    return -1
  }

  const curMonthSegment = curDate.diff(startDate, 'months')
  const lastDayOfCurMonth = moment(curDate).endOf('month')

  const fractionByCurDay = (curDate.date() - 1) / (lastDayOfCurMonth.date() - 1) / monthDiff

  const offset = curMonthSegment / monthDiff + fractionByCurDay

  return offset
}

const generateCampaignDateFractions = (data, start, end, startDateKey, endDateKey) => {
  const startDate = moment(start)
  const endDate = moment(end).endOf('month')
  const monthDiff = endDate.diff(startDate, 'months') + 1

  const fractions = []

  let inprogressCount = 0
  let inactiveCount = 0
  let completeCount = 0
  let cancelCount = 0

  data.forEach(d => {
    // eslint-disable-next-line no-unused-vars
    const { status, trainingCampName: name } = d
    const campaignStartDate = moment(d[startDateKey])
    const campaignEndDate = moment(d[endDateKey])

    switch (status) {
      case 'inprogress':
      case 'IP':
        inprogressCount += 1
        break
      case 'complete':
      case 'COM':
      case 'EC':
        completeCount += 1
        break
      case 'cancel':
      case 'CAN':
        cancelCount += 1
        break
      default:
        inactiveCount += 1
        break
    }

    const startMonthSegment = campaignStartDate.diff(startDate, 'months')
    const endMonthSegment = campaignEndDate.diff(startDate, 'months')

    const lastDayOfStartMonth = moment(campaignStartDate).endOf('month')
    const lastDayOfEndMonth = moment(campaignEndDate).endOf('month')

    const fractionByStartDay =
      ((campaignStartDate.date() - 1) * 24 + campaignStartDate.hour()) /
      ((lastDayOfStartMonth.date() - 1) * 24) /
      monthDiff

    const fractionByEndDay =
      ((campaignEndDate.date() - 1) * 24 + campaignEndDate.hour()) /
      ((lastDayOfEndMonth.date() - 1) * 24) /
      monthDiff

    const offset = startMonthSegment / monthDiff + fractionByStartDay
    const length = endMonthSegment / monthDiff + fractionByEndDay - offset

    const dateFractionData = { offset, length, status }

    if (moment().isAfter(campaignStartDate)) {
      dateFractionData.progressLength = getCursorFraction(start, end) - offset
    }

    fractions.push(dateFractionData)
  })

  return {
    fractions,
    count: {
      complete: completeCount,
      inactive: inactiveCount,
      inprogress: inprogressCount,
      cancel: cancelCount,
    },
  }
}

const generateChartData = (data, startDateKey, endDateKey, startDate, endDate) => {
  if (isEmpty(data)) {
    return null
  }

  const months = generateMonths(startDate, endDate)
  const earliestDate = months[0]
  const latestDate = months[months.length - 1]
  const curFraction = getCursorFraction(earliestDate, latestDate)
  const { fractions, count } = generateCampaignDateFractions(
    data,
    earliestDate,
    latestDate,
    startDateKey,
    endDateKey,
  )

  return { months, curFraction, campaignDateFractions: fractions, statusCount: count }
}

const CampaignGanttChart = ({
  data = [],
  childrenData,
  statusTotal = {},
  statusMap = {
    inProgressNum: 'inProgressNum',
    completeNum: 'completeNum',
    cancelNum: 'cancelNum',
    notStartNum: 'notStartNum',
  },
  idKey,
  nameKey,
  startDateKey,
  endDateKey,
  childrenKey,
  selectedId,
  childrenCompare,
  height = 'auto',
  onRowClick,
  onChildRowClick,
  onExpand,
  loading,
  pagination = {},
  startDate,
  endDate,
}) => {
  const { totalPages, page, pageSize, onPageChange, onPageSizeChange } = pagination
  const notStartNum = statusTotal[statusMap.notStartNum] || 0
  const inProgressNum = statusTotal[statusMap.inProgressNum] || 0
  const completeNum = statusTotal[statusMap.completeNum] || 0
  // const cancelNum = statusTotal[statusMap.cancelNum] || 0

  const { t } = useTranslation()
  const [chartData, setChartData] = useState({})
  const [displayWidth, setDisplayWidth] = useState(0)
  const displayRef = useRef()
  const barContainerRef = useRef()
  const mouseOrigin = useRef()
  const initialWidth = useRef()
  const [scrollbarWidth, setScrollbarWidth] = useState(0)
  const [nameColWidth, setNameColWidth] = useState(NAME_COL_WIDTH)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const throttleWidth = useCallback(
    throttle(params => {
      if (inRange(params, 50, NAME_COL_WIDTH * 5)) setNameColWidth(params)
    }, 1),
    [],
  )

  const onMouseMove = e => {
    e.preventDefault()

    const diffX = e.clientX - mouseOrigin.current.x

    const newWidth = initialWidth.current + diffX

    throttleWidth(newWidth)
  }

  const onMouseUp = () => {
    document.removeEventListener('pointerup', onMouseUp)
    document.removeEventListener('pointermove', onMouseMove)
  }

  const onMouseDown = e => {
    document.addEventListener('pointerup', onMouseUp)
    document.addEventListener('pointermove', onMouseMove)

    mouseOrigin.current = { x: e.clientX, y: e.clientY }
    initialWidth.current = nameColWidth
  }

  useEffect(() => {
    const displayElement = displayRef.current
    const scrollElement = barContainerRef.current

    const displayObserver = new ResizeObserver(entries => {
      setDisplayWidth(entries[0].contentRect.width)
    })

    const scrollObserver = new ResizeObserver(() => {
      const { offsetWidth = 0, clientWidth = 0 } = scrollElement || {}
      setScrollbarWidth(offsetWidth - clientWidth)
    })

    displayObserver.observe(displayElement)
    scrollObserver.observe(scrollElement)

    return () => {
      displayObserver.unobserve(displayElement)
      scrollObserver.unobserve(scrollElement)
    }
  }, [])

  useEffect(() => {
    const allData = []

    data.forEach(d => {
      allData.push(d)
      if (d[idKey]?.toString() === selectedId?.toString()) {
        childrenData?.[d[idKey]]?.forEach(child => {
          allData.push(child)
        })
      }
    })

    setChartData(generateChartData(allData, startDateKey, endDateKey, startDate, endDate))
  }, [data, idKey, selectedId, startDateKey, endDateKey, startDate, endDate, childrenData])

  const { months = [], curFraction = 0, campaignDateFractions = [] } = chartData || {}

  return (
    <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', height }}>
      <Box
        sx={{
          flex: 1,
          color: theme => theme.palette.secondary[80].main,
          overflowX: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            opacity: data.length === 0 ? 0 : 100,
          }}
        >
          <Box display="flex">
            <BorderedSection
              width={nameColWidth}
              sx={{ backgroundColor: theme => theme.palette.secondary[80].main, border: 'none' }}
            >
              <PaddedText
                typographyProps={{
                  sx: {
                    color: theme => theme.palette.other.white.main,
                  },
                }}
              >
                {t('general.campaign_name')}
              </PaddedText>
            </BorderedSection>
            {months.map((m, index) => (
              <DateHeader
                key={`campaignGanttChart_dateHeader_${index * 2}`}
                isLast={!(index < months.length - 1)}
              >
                {moment(m).format(DATE_MONTH_FORMAT)}
              </DateHeader>
            ))}

            <BorderedSection
              sx={{
                width: scrollbarWidth,
                backgroundColor: theme => theme.palette.secondary[80].main,
                border: 'none',
              }}
            />
          </Box>
          <Box
            className={globalLess.primaryScrollbar}
            sx={{
              display: 'flex',
              position: 'relative',
              flex: 1,
              overflow: 'hidden',
            }}
          >
            <Box
              ref={barContainerRef}
              sx={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              <Box display="flex" minHeight="100%">
                <BorderedSection
                  width={nameColWidth}
                  sx={{ borderWidth: '0px 1px 0px 0px', position: 'relative' }}
                >
                  {data.map((d, index) => (
                    <Stack key={`campaignGanttChart_name_${index * 2}`}>
                      <RowHeader
                        height={ROW_HEIGHT}
                        onClick={() => onRowClick(d)}
                        onExpand={() => onExpand?.(d)}
                        label={d[nameKey]}
                        hasChildren={childrenKey && childrenCompare?.(d[childrenKey])}
                        selected={d[idKey]?.toString() === selectedId?.toString()}
                      />
                      {d[idKey]?.toString() === selectedId?.toString() &&
                        childrenData?.[d[idKey]]?.map((child, childIndex) => (
                          <Box key={`child_${childIndex + 1}`} pl={1}>
                            <RowHeader
                              height={ROW_HEIGHT}
                              onClick={() => onChildRowClick?.({ ...d, ...child })}
                              label={child.campaignName}
                            />
                          </Box>
                        ))}
                    </Stack>
                  ))}
                  <Box
                    sx={{
                      cursor: 'col-resize',
                      position: 'absolute',
                      width: 10,
                      right: -5,
                      top: 0,
                      bottom: 0,
                    }}
                    onDragStart={e => {
                      e.preventDefault()
                    }}
                    onMouseDown={onMouseDown}
                    onMouseUp={onMouseUp}
                  />
                </BorderedSection>

                <BorderedSection
                  position="relative"
                  flex={1}
                  sx={{ borderWidth: '0px 0px 0px 0px', pointerEvents: 'none' }}
                >
                  <Box
                    sx={{ display: 'flex', position: 'absolute', height: '100%', width: '100%' }}
                  >
                    {months.map((_, index) => (
                      <EmptyColumn
                        flex={1}
                        key={`campaignGanttChart_emptyCol_${index * 2}`}
                        isLast={!(index < months.length - 1)}
                      />
                    ))}
                  </Box>

                  <Box
                    ref={displayRef}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'absolute',
                      height: '100%',
                      width: '100%',
                    }}
                  >
                    {campaignDateFractions.map((d, index) => (
                      <DateBar
                        height={ROW_HEIGHT}
                        key={`campaignGanttChart_dateBars_${index * 2}`}
                        data={d}
                        displayWidth={displayWidth}
                      />
                    ))}
                  </Box>

                  <Box
                    sx={{
                      position: 'absolute',
                      height: '100%',
                      width: 5,
                      backgroundColor: '#00000040',
                      top: 0,
                      left: displayWidth * curFraction - 2.5,
                    }}
                  />
                </BorderedSection>
              </Box>
            </Box>
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                boxShadow: 'inset 0px -1px 0px 0px',
                pointerEvents: 'none',
              }}
            />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{ minHeight: 52, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}
      >
        <Pagination
          pageCount={totalPages}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          page={page}
          pageSize={pageSize}
          sideComponent={
            <Stack direction="row" justifyContent="flex-end" spacing={2} padding={2}>
              <LegendItem
                color={COLOR.COMPLETE_F}
                label={`${t('general.completed')} (${completeNum})`}
              />
              <LegendItem
                color={COLOR.INPROGRESS_F}
                label={`${t('general.inprogress')} (${inProgressNum})`}
              />
              <LegendItem
                color={COLOR.INACTIVE_F}
                label={`${t('general.pending')} (${notStartNum})`}
              />
              {/* <LegendItem color={COLOR.CANCEL_F} label={`${t('general.cancel')} (${cancelNum})`} /> */}
            </Stack>
          }
        />
      </Box>

      {(loading || data.length === 0) && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.38)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box mt={-10}>
            {loading ? (
              <CircularProgress />
            ) : (
              <Typography variant="h6">{t('errors.no_campaign')}</Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default CampaignGanttChart
